import { BorderLeft, Padding } from "@mui/icons-material";
import { createTheme } from "@mui/material/styles";
import typography from './fontSize'
declare module "@mui/material/styles" {
  interface Palette {
    linkActive: string;
    tableSecondary: string;
    selected: string;
    linkInactive: string;
    border: string;
  }
  interface PaletteOptions {
    linkActive?: string;
    tableSecondary: string;
    selected: string;
    linkInactive?: string;
    border: string;
  }
}

export const lightTheme = () =>
  createTheme({
    palette: {
      mode: "light",
      primary: {
        main: "#5547DD",
      },
      secondary: {
        main: "#767676", // Define secondary color centrally
      },
      error: {
        main: "#C83333", // Define error color for badge
      },
      linkInactive: '#595959',
      tableSecondary: '#2B4D90',
      background: {
        default: "#fff",
        paper: "#fff",
      },
      selected: '#EEEDFC',
      border: '#F0EFF8',
      text: {
        primary: "#1B1B1B",
        secondary: "#333333", // Define secondary text centrally
        disabled: "#eaeaea",
      },
      action: {
        hover: "#f4f4f4",
        selected: "#e0e0e0",
        disabled: "#b3a1b3",
      },
    },
    typography,
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '12px'
          }
        }
      },
      MuiTable: {
        styleOverrides: {
          root: {
            borderSpacing: '0 8px', // Adds space between rows
            borderCollapse: 'separate', // Required for spacing to work
            overflow: 'hidden', // Ensures border-radius applies correctly
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            border: '2px solid #F0EFF8', // Border around the row
            '&:first-of-type': {
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
            },
            '&:last-of-type': {
              borderBottomLeftRadius: '8px',
              borderBottomRightRadius: '8px',
            },

            "&:nth-of-type(even)": {
              backgroundColor: "#F6F6FD", // Light purple for even rows
            },
            "&:nth-of-type(odd)": {
              backgroundColor: "#FFFFFF", // White for odd rows
            },


          }
        }
      },
      MuiModal: {
        styleOverrides: {
          root: {
            "& .MuiBox-root:focus-visible": {
              outline: 'none',
            },
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableRow-root': {
              backgroundColor: '#F6F6FD !important', // Ensures header row is styled properly

            },
            '& .MuiTableCell-root': {
              padding: '12px 16px'
            }
          }
        }
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            
            '& .MuiTableCell-root': {
              padding: '12px 16px'
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '12px 16px',
            borderTop: '2px solid #F0EFF8', // Top border for all cells
            '&:first-of-type': {
              borderLeft: '2px solid #F0EFF8', // Left border for the first cell
              borderTopLeftRadius: '8px', // Rounded corner
              borderBottomLeftRadius: '8px',
            },
            '&:last-of-type': {
              borderRight: '2px solid #F0EFF8', // Right border for the last cell
              borderTopRightRadius: '8px', // Rounded corner
              borderBottomRightRadius: '8px',
            },
           
          }
        }
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: "none", // Removed box shadow
            borderRadius: '0px'
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontWeight: 600,
            fontSize: "16px",
            lineHeight: "24px",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            padding: '12px',
            margin: '0px',
            marginBottom: '0px',
            gap: '8px',
            alignItems: 'center',
            justifyContent: 'start'
          }
        }
      }, MuiLink: {
        styleOverrides: {
          root: {
            fontSize: "14px",
            fontWeight: 700,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "10px",
            fontFamily: "Inter Tight, sans-serif",
            textTransform: 'none',
            fontWeight: 700,
            fontSize: "14px",
            lineHeight: "21px",
            padding: "12px 20px",
            "&.Mui-disabled": {
              cursor: "not-allowed",
            },
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontSize: "8px",
            borderRadius: "4px",
            background: "inherit", // Use theme error color
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: "8px 12px",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "inherit", // Use theme hover color
            },
            "&.Mui-selected": {
              backgroundColor: "inherit", // Use theme selected color
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: "10px",
            minWidth: "160px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            padding: "6px 8px",
          },
        },
      },
    },
  });


export const darkTheme = () =>
  createTheme({
    palette: {
      mode: "dark",
      primary: {
        main: "#8C7CF0",
      },
      secondary: {
        main: "#E3E3E3",
      },
      error: {
        main: "#FF6B6B",
      },
      linkInactive: "#B3B3B3",
      tableSecondary: "#2B4D90",
      border: '#F0EFF8',
      background: {
        default: "#121212",
        paper: "#1E1E1E",
      },
      selected: '#34316E',
      text: {
        primary: "#EDEDED",
        secondary: "#EBEBEB",
        disabled: "#5A5A5A",
      },
      action: {
        hover: "#2A2A2A",
        selected: "#3A3A3A",
        disabled: "#7A7A7A",
      },
    },
    typography,
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: "12px",
            backgroundColor: "#1E1E1E",
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            borderSpacing: "0 8px",
            borderCollapse: "separate",
            overflow: "hidden",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            border: "2px solid #2A2A2A",
            "&:first-of-type": {
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            },
            "&:last-of-type": {
              borderBottomLeftRadius: "8px",
              borderBottomRightRadius: "8px",
            },
            "&:nth-of-type(even)": {
              backgroundColor: "#2A2A2A",
            },
            "&:nth-of-type(odd)": {
              backgroundColor: "#1E1E1E",
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableRow-root": {
              backgroundColor: "#2A2A2A !important",
            },
            "& .MuiTableCell-root": {
              padding: "12px 16px",
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderTop: "2px solid #2A2A2A",
            "&:first-of-type": {
              borderLeft: "2px solid #2A2A2A",
              borderTopLeftRadius: "8px",
              borderBottomLeftRadius: "8px",
            },
            "&:last-of-type": {
              borderRight: "2px solid #2A2A2A",
              borderTopRightRadius: "8px",
              borderBottomRightRadius: "8px",
            },
            "& .MuiTableCell-root": {
              padding: '12px 16px',
            },
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            
            '& .MuiTableCell-root': {
              padding: '12px 16px'
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            borderRadius: "0px",
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontWeight: 600,
            fontSize: "16px",
            lineHeight: "24px",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            padding: "12px",
            margin: "0px",
            marginBottom: "0px",
            gap: "8px",
            alignItems: "center",
            justifyContent: "start",
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            fontSize: "14px",
            fontWeight: 700,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "10px",
            fontFamily: "Inter Tight, sans-serif",
            textTransform: "none",
            fontWeight: 700,
            fontSize: "14px",
            lineHeight: "21px",
            padding: "12px 20px",
            backgroundColor: "#2A2A2A",
            color: "#EDEDED",
            "&.Mui-disabled": {
              cursor: "not-allowed",
            },
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontSize: "8px",
            borderRadius: "4px",
            background: "inherit",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: "8px 12px",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "#2A2A2A",
            },
            "&.Mui-selected": {
              backgroundColor: "#3A3A3A",
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: "10px",
            minWidth: "160px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
            padding: "6px 8px",
          },
        },
      },
    },
  });


